import type { AntibodyResponse, SequenceData } from '../types';

/**
 * Sequence Helper Functions
 *
 * Utilities for working with the chain-separated sequence structure.

 */

/**
 * Get the chain type for a sequence
 * @returns 'IGH', 'IGK', 'IGL', 'Paired', or null
 */
export function getSequenceChainType(sequence: SequenceData): 'IGH' | 'IGK' | 'IGL' | 'Paired' | null {
  if (sequence.heavyChain && sequence.lightChain) {
    return 'Paired';
  }
  if (sequence.heavyChain) {
    return sequence.heavyChain.chainType as 'IGH';
  }
  if (sequence.lightChain) {
    return sequence.lightChain.chainType as 'IGK' | 'IGL';
  }
  return null;
}

/**
 * Get the full sequence string (prioritizes heavy chain if both exist)
 */
export function getSequenceString(sequence: SequenceData): string {
  return sequence.heavyChain?.sequence || sequence.lightChain?.sequence || '';
}

/**
 * Get the regions for a sequence (prioritizes heavy chain if both exist)
 */
export function getSequenceRegions(sequence: SequenceData | undefined | null) {
  if (!sequence) {
    return null;
  }
  return sequence.heavyChain?.regions || sequence.lightChain?.regions || null;
}

/**
 * Check if a clonotype matches a specific chain filter
 */
export function matchesChainFilter(clonotype: AntibodyResponse, chainType: 'all' | 'heavy' | 'light'): boolean {
  if (chainType === 'all') {
    return true;
  }

  return clonotype.sequences.some((seq) => {
    const seqChainType = getSequenceChainType(seq);
    if (chainType === 'heavy') {
      return seqChainType === 'IGH' || seqChainType === 'Paired';
    }
    return seqChainType === 'IGK' || seqChainType === 'IGL' || seqChainType === 'Paired';
  });
}
